import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import { Navbar } from './components';
import { Dashboard, CardEditor, CardsList, Wallet } from './pages';
import './App.css';
import { LogIn } from 'lucide-react';

const msalInstance = new PublicClientApplication(msalConfig);

const LoginPage: React.FC = () => {
  const handleLogin = () => {
    // Use redirect instead of popup for mobile compatibility
    msalInstance.loginRedirect({
      scopes: ['User.Read'],
    });
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-icon">
          <LogIn size={64} />
        </div>
        <h1>Digital Business Card</h1>
        <p>Sign in with your Microsoft account to get started</p>
        <button onClick={handleLogin} className="btn btn-primary btn-login">
          <LogIn size={20} />
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize MSAL and handle redirect promise
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
        setIsInitialized(true);
      } catch (error) {
        console.error('MSAL initialization error:', error);
        setIsInitialized(true); // Still render the app even if there's an error
      }
    };

    initializeMsal();
  }, []);

  // Show loading while MSAL initializes
  if (!isInitialized) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-icon">
            <LogIn size={64} />
          </div>
          <h1>Digital Business Card</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <div className="app">
          {/* Authenticated Routes */}
          <AuthenticatedTemplate>
            <Navbar />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/cards" element={<CardsList />} />
                <Route path="/cards/new" element={<CardEditor />} />
                <Route path="/cards/edit/:cardId" element={<CardEditor />} />
                <Route path="/cards/view/:cardId" element={<CardEditor />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </AuthenticatedTemplate>

          <UnauthenticatedTemplate>
            <Routes>
              <Route path="*" element={<LoginPage />} />
            </Routes>
          </UnauthenticatedTemplate>
        </div>
      </BrowserRouter>
    </MsalProvider>
  );
};

export default App;
